import { useEffect, useState, useMemo } from "react";
import { Copy, Check, GitBranch, Terminal } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { conversionSetup } from "../lib/api";
import { useDisplayCurrency } from "../lib/currency";
import { COPY_INDICATOR_MS } from "../lib/constants";

function CopyBlock({ label, snippet, testid }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), COPY_INDICATOR_MS);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono">{label}</div>
        <button
          data-testid={testid}
          onClick={doCopy}
          className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 hover:text-[#ccff00] flex items-center gap-1 transition-colors"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "copied" : "copy"}
        </button>
      </div>
      <pre
        className="bg-[#050505] border border-white/10 p-3 font-mono text-[10.5px] text-zinc-300 leading-relaxed overflow-x-auto whitespace-pre-wrap break-all"
        style={{ maxHeight: 200 }}
      >
        {snippet}
      </pre>
    </div>
  );
}

/**
 * Setup tracking dialog — shows webhook URL, JS pixel snippet, and manual curl.
 * Users drop the pixel on affiliate thank-you pages or wire the webhook into
 * their affiliate network's postback config.
 */
export default function ConversionSetupDialog({
  open,
  onOpenChange,
  adSetId,
}) {
  const workspaceCcy = useDisplayCurrency(open);
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !adSetId) return;
    let cancelled = false;
    setLoading(true);
    conversionSetup(adSetId)
      .then((data) => {
        if (!cancelled) setSetup(data);
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Failed to load setup");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, adSetId]);

  const pixelSnippet = useMemo(() => {
    if (!setup) return "";
    return `<!-- AdForge conversion pixel — drop on affiliate thank-you page -->
<script src="${setup.pixel_url}"></script>
<script>
  AdForge.trackConversion({
    value: 49.99,               // your order total
    currency: '${workspaceCcy}',
    external_id: 'ORDER_ID_HERE' // any unique id (dedupes)
  });
</script>`;
  }, [setup, workspaceCcy]);

  const pixelAutoFire = useMemo(() => {
    if (!setup) return "";
    return `<!-- Auto-fires on load using data-* attributes (no JS calls) -->
<script src="${setup.pixel_url}"
  data-af-value="49.99"
  data-af-currency="${workspaceCcy}"
  data-af-external-id="ORDER_ID_HERE"
  data-af-auto-fire="true"></script>`;
  }, [setup, workspaceCcy]);

  const webhookCurl = useMemo(() => {
    if (!setup) return "";
    const first = setup.variants?.[0];
    return `# S2S postback (paste into your affiliate network's postback URL)
curl -X POST "${setup.webhook_url}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "click_id": "{click_id_from_af_click_id_param}",
    "value": 49.99,
    "currency": "${workspaceCcy}",
    "external_id": "{order_id}",
    "source": "webhook"
  }'

# — OR — reference the variant directly
curl -X POST "${setup.webhook_url}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "ad_set_id": "${setup.ad_set_id}",
    "platform": "${first?.platform || "google"}",
    "variant_id": "${first?.variant_id || ""}",
    "value": 49.99,
    "currency": "${workspaceCcy}",
    "external_id": "{order_id}",
    "source": "webhook"
  }'`;
  }, [setup, workspaceCcy]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0a0a0a] border border-white/10 rounded-none max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="conversion-setup-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <GitBranch size={22} weight="fill" className="text-[#ccff00]" />
            Conversion tracking setup
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Pick any one method. Conversions immediately show up on the A/B
            leaderboard and rank variants by <span className="text-[#ccff00]">CVR &amp; revenue</span>.
            Snippets default to your workspace currency (<span className="text-[#ccff00] font-mono">{workspaceCcy}</span>) — override the <code className="font-mono">currency</code> field per sale if needed.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-center text-zinc-500 font-mono text-sm py-6">
            loading…
          </div>
        )}

        {setup && !loading && (
          <div className="space-y-5 py-2">
            <div className="bg-[#050505] border border-white/5 p-3 flex items-center gap-2 text-xs font-mono">
              <Terminal size={13} className="text-[#ccff00]" />
              <span className="text-zinc-500">tracker URLs already inject</span>
              <code className="text-[#ccff00]">?af_click_id=…</code>
              <span className="text-zinc-500">→ pixel auto-resolves.</span>
            </div>

            <CopyBlock
              label="① JS pixel · manual fire"
              snippet={pixelSnippet}
              testid="copy-pixel-manual"
            />

            <CopyBlock
              label="② JS pixel · auto-fire (no code)"
              snippet={pixelAutoFire}
              testid="copy-pixel-auto"
            />

            <CopyBlock
              label="③ Server-to-server postback (curl)"
              snippet={webhookCurl}
              testid="copy-webhook-curl"
            />

            <div className="pt-3 border-t border-white/5">
              <div className="label-mono mb-2">webhook URL</div>
              <div className="bg-[#050505] border border-white/10 p-3 font-mono text-xs text-[#ccff00] break-all">
                {setup.webhook_url}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
